'use client'
import React, { useEffect, useState } from 'react'
import { compact, filter, includes, isBoolean, map } from 'lodash'
import { ErrorLike } from '@apollo/client'

import { useAdminPracticeBase } from '@docpace/admin-react-hooks'
import { isProdInferredFromLocation } from '@docpace/shared-react-utils'
import { PracticeStatus } from '@docpace/shared-ts-types'
import { useAdminAdminDetailQuery, useAdminBaseQuery, useAdminQueueAthenaPracticeSetUpMutation, useAdminQueueRefreshPracticeAppointmentTypeStatsMutation } from '@docpace/admin-react-apollo'
import { ctxPracticeIdAtom } from '@docpace/shared-react-atoms'
import { useAtom } from 'jotai'

interface UseAdminBaseProps {
    skip?: boolean
}

export function useAdminBase(props?: UseAdminBaseProps){
    const { skip } = props ?? {}
    
    const [ practiceId ] = useAtom(ctxPracticeIdAtom)
    const [fetchError, setFetchError] = useState<ErrorLike | null>(null)
    const [queueAthenaPracticeSetup] = useAdminQueueAthenaPracticeSetUpMutation()
    const [queueRefreshPracticeAppointmentTypeStats] =
        useAdminQueueRefreshPracticeAppointmentTypeStatsMutation()
    
    const {
        loading: adminBaseLoading,
        refetch: adminBaseRefetch,
        data: adminBaseData,
        error: adminBaseError,
    } = useAdminBaseQuery({
        skip: !!fetchError || skip,
    })

    const {
        practices: practicesData,
        adminId,
        // practiceId,
        serverInfo,
        practiceFileBucket,
        practiceFileRegion,
    } = adminBaseData || {}
        
    const {
        isLoading: practiceLoading,
        practice,
        providers,
        departments,
        appointmentTypes,
    } = useAdminPracticeBase({ skip, practiceId })

    const { loading: adminDetailLoading, data: adminData, error: adminDetailError } =
        useAdminAdminDetailQuery({
            variables: { adminId: adminId ?? '' },
            skip: !adminId || !!fetchError || skip,
        })

    useEffect(() => {
        const error = adminBaseError ?? adminDetailError
        if (error) setFetchError(error)
    }, [adminBaseError, adminDetailError])
        

    const allPractices = compact(practicesData?.nodes)
    const practices = filter(
        allPractices,
        ({ status }) => status !== PracticeStatus.DISABLED
    )
    const disabledPractices = filter(
        allPractices,
        ({ status }) => status === PracticeStatus.DISABLED
    )

    return {
        adminId: adminData?.admin?.adminId ?? null,
        practice: practice ?? null,
        allPractices,
        practices,
        disabledPractices,
        admin: adminData?.admin ?? null,
        actor: adminData?.admin?.adminActor ?? null,
        isProd: isBoolean(serverInfo?.isProd) ? serverInfo?.isProd : isProdInferredFromLocation,
        webhooksServerUrl: serverInfo?.webhooksServerUrl,
        adminBaseRefetch: ()=>adminBaseRefetch().then(()=>{}),
        queueAthenaPracticeSetup,
        queueRefreshPracticeAppointmentTypeStats,
        googleMapsApiKey: serverInfo?.googleMapsApiKey,
        practiceFileBucket,
        practiceFileRegion,
        isLoading:
            adminBaseLoading ||
            practiceLoading ||
            adminDetailLoading
    }
}

export default useAdminBase
